/*
 * VELTRON 2027 - menu mobile : composant <vt-menu>
 *
 * Sans dependance. Le panneau est un <dialog> ouvert avec showModal() : il passe en couche superieure
 * (insensible a "contain: layout" de l'en-tete), le reste de la page devient inerte et le focus reste dans le menu.
 *
 *  - ouverture / fermeture animees (la duree vient du CSS ; aucune animation si mouvement reduit)
 *  - fermeture : bouton, fond assombri, Echap, clic sur un lien, retour arriere du navigateur (bfcache)
 *  - sous-menus : glissement avec bouton Retour, seulement si un lien possede des enfants
 *  - focus : sur "Fermer" a l'ouverture, retour sur le bouton du menu a la fermeture
 *  - defilement de la page bloque pendant l'ouverture (classe html.vt-menu-open)
 *  - navigateurs sans <dialog>.showModal() : le panneau est deplace dans <body> pendant l'ouverture
 *  - langue : au moment de l'envoi du formulaire Shopify, l'adresse de retour est recalculee a partir de l'adresse reelle de la
 *    page (sans prefixe de langue, sans section_id / sections) ; double envoi bloque ; retour arriere du navigateur gere
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('vt-menu')) return;

  var ROOT = document.documentElement;
  var LOCK_CLASS = 'vt-menu-open';
  var CLOSE_DELAY = 230; /* un peu plus que la transition CSS de fermeture (200 ms) */
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function focusSafely(element) {
    if (!element || typeof element.focus !== 'function') return;
    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  }

  /*
   * Adresse de retour du formulaire de langue : la page courante, sans le prefixe de langue actuel (Shopify l'ajoute lui-meme
   * pour la nouvelle langue) et sans les parametres techniques "section_id" / "sections" que le theme ajoute quand il recharge
   * l'en-tete par l'API de rendu de sections. Les autres parametres (variante, tri, page, apercu du theme...) sont conserves.
   */
  function returnPath() {
    var path = window.location.pathname || '/';
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.length > 1) {
      var bare = root.replace(/\/+$/, '');
      if (bare && (path === bare || path.indexOf(bare + '/') === 0)) path = path.slice(bare.length) || '/';
    }
    /* le texte de la requete est filtre tel quel (pas de re-encodage) : seuls section_id et sections disparaissent */
    var query = (window.location.search || '').replace(/^\?/, '').split('&').filter(function (pair) {
      return pair && !/^(section_id|sections)(=|$)/.test(pair);
    }).join('&');
    return path + (query ? '?' + query : '');
  }

  class VtMenu extends HTMLElement {
    constructor() {
      super();
      this.ready = false;
      this.onPageShow = this.onPageShow.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.clearBusy = this.clearBusy.bind(this);
      this.open = this.open.bind(this);
      this.finish = this.finish.bind(this);
      this.busyTimer = 0;
    }

    connectedCallback() {
      this.setup();
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.busyTimer);
      if (this.ready && this.isOpen()) this.finish();
    }

    setup() {
      if (this.ready) return;

      this.dialog = this.querySelector('.vt-menu__dialog');
      this.trigger = this.querySelector('.vt-menu__trigger');
      this.sheet = this.querySelector('.vt-menu__sheet');
      this.body = this.querySelector('.vt-menu__body');
      this.closeButton = this.querySelector('.vt-menu__close');
      this.rootPanel = this.querySelector('[data-vt-panel="root"]');
      if (!this.dialog || !this.trigger || !this.sheet || !this.body || !this.rootPanel) return;

      this.panels = Array.prototype.slice.call(this.dialog.querySelectorAll('[data-vt-panel]'));
      this.stack = []; /* sous-menus ouverts : [{ panel, origin }] */
      this.timer = 0;
      this.home = null; /* place d'origine du panneau quand il est deplace dans <body> */
      this.modal = typeof this.dialog.showModal === 'function';

      this.trigger.addEventListener('click', this.open);
      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('submit', this.onSubmit);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.finish);
      this.body.addEventListener('scroll', this.onScroll, { passive: true });
      if (!this.modal) this.dialog.addEventListener('keydown', this.onKeyDown);

      this.ready = true;
    }

    isOpen() {
      return this.dialog.hasAttribute('open');
    }

    /* ---- Ouverture / fermeture ------------------------------------------- */

    open() {
      if (!this.ready || this.isOpen()) return;
      clearTimeout(this.timer);
      this.resetPanels();
      this.dialog.setAttribute('data-state', 'opening');

      if (this.modal) {
        this.dialog.showModal();
      } else {
        this.home = { parent: this.dialog.parentNode, next: this.dialog.nextSibling };
        document.body.appendChild(this.dialog);
        this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('aria-modal', 'true');
      }

      ROOT.classList.add(LOCK_CLASS);
      this.trigger.setAttribute('aria-expanded', 'true');
      this.onScroll();
      focusSafely(this.closeButton);

      /* deux images : le navigateur peint d'abord l'etat de depart, puis la transition demarre */
      var dialog = this.dialog;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (dialog.getAttribute('data-state') === 'opening') dialog.setAttribute('data-state', 'open');
        });
      });
    }

    close(instant) {
      if (!this.ready || !this.isOpen()) return;
      if (this.dialog.getAttribute('data-state') === 'closing') return;

      clearTimeout(this.timer);
      this.dialog.setAttribute('data-state', 'closing');

      if (instant === true || reduceMotion.matches) {
        this.finish();
        return;
      }
      this.timer = window.setTimeout(this.finish, CLOSE_DELAY);
    }

    /* Remet tout a zero. Appele a la fin de l'animation, par l'evenement "close" natif, ou si le menu est retire de la page */
    finish() {
      if (!this.ready) return;
      clearTimeout(this.timer);

      if (this.isOpen()) {
        if (typeof this.dialog.close === 'function') this.dialog.close();
        else this.dialog.removeAttribute('open');
      }
      if (this.home && this.home.parent) {
        this.home.parent.insertBefore(this.dialog, this.home.next);
        this.dialog.removeAttribute('aria-modal');
      }
      this.home = null;

      var wasOpen = this.dialog.getAttribute('data-state') !== 'closed';
      this.dialog.setAttribute('data-state', 'closed');
      ROOT.classList.remove(LOCK_CLASS);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.resetPanels();
      if (wasOpen) focusSafely(this.trigger);
    }

    /* ---- Sous-menus ------------------------------------------------------ */

    resetPanels() {
      for (var i = 0; i < this.stack.length; i += 1) this.stack[i].origin.setAttribute('aria-expanded', 'false');
      this.stack = [];
      this.showPanel(this.rootPanel, '');
    }

    showPanel(panel, direction) {
      for (var i = 0; i < this.panels.length; i += 1) {
        var item = this.panels[i];
        item.hidden = item !== panel;
        item.removeAttribute('data-dir');
      }
      if (direction && !reduceMotion.matches) panel.setAttribute('data-dir', direction);
      this.body.setAttribute('data-view', panel === this.rootPanel ? 'root' : 'sub');
      this.body.scrollTop = 0;
      this.onScroll();
    }

    go(id, origin) {
      var panel = this.dialog.querySelector('#' + id);
      if (!panel) return;
      this.stack.push({ panel: panel, origin: origin });
      origin.setAttribute('aria-expanded', 'true');
      this.showPanel(panel, 'forward');
      focusSafely(panel.querySelector('.vt-menu__back'));
    }

    back() {
      var last = this.stack.pop();
      var previous = this.stack.length ? this.stack[this.stack.length - 1].panel : this.rootPanel;
      this.showPanel(previous, 'back');
      if (last && last.origin) {
        last.origin.setAttribute('aria-expanded', 'false');
        focusSafely(last.origin);
      }
    }

    /* ---- Evenements ------------------------------------------------------ */

    onClick(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('[data-vt-close]')) {
        this.close();
        return;
      }

      var forward = target.closest('[data-vt-go]');
      if (forward) {
        this.go(forward.getAttribute('data-vt-go'), forward);
        return;
      }

      if (target.closest('[data-vt-back]')) {
        this.back();
        return;
      }

      var link = target.closest('a[href]');
      if (link && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        /* le lien suit son cours (navigation) ; on referme le panneau tout de suite pour que ce soit propre */
        this.close();
      }
    }

    /* Changement de langue : on fixe l'adresse de retour juste avant l'envoi, au lieu de se fier a la valeur du HTML (que le
       rechargement de l'en-tete par le theme pouvait avoir remplacee par une adresse technique), et on bloque le double envoi.
       Le bouton touche n'est surtout pas desactive ici : le navigateur n'enverrait plus language_code. */
    onSubmit(event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('vt-menu__langform')) return;

      if (form.getAttribute('aria-busy') === 'true') {
        event.preventDefault();
        return;
      }

      var field = form.querySelector('input[name="return_to"]');
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'return_to';
        form.appendChild(field);
      }
      field.value = returnPath();

      form.setAttribute('aria-busy', 'true');
      if (event.submitter && event.submitter.classList) event.submitter.classList.add('is-loading');
      window.clearTimeout(this.busyTimer);
      this.busyTimer = window.setTimeout(this.clearBusy, 10000); /* reseau coupe : le formulaire redevient utilisable */
    }

    clearBusy() {
      window.clearTimeout(this.busyTimer);
      if (!this.dialog) return;
      var forms = this.dialog.querySelectorAll('.vt-menu__langform[aria-busy]');
      for (var i = 0; i < forms.length; i += 1) forms[i].removeAttribute('aria-busy');
      var items = this.dialog.querySelectorAll('.vt-menu__seg-item.is-loading');
      for (var j = 0; j < items.length; j += 1) items[j].classList.remove('is-loading');
    }

    /* Echap : on recule d'un niveau dans un sous-menu, sinon on ferme */
    onCancel(event) {
      event.preventDefault();
      if (this.stack.length) this.back();
      else this.close();
    }

    /* Navigateurs sans <dialog> modal : Echap gere a la main */
    onKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') this.onCancel(event);
    }

    onScroll() {
      if (!this.sheet) return;
      if (this.body.scrollTop > 2) this.sheet.setAttribute('data-scrolled', '');
      else this.sheet.removeAttribute('data-scrolled');
    }

    /* Retour avec le bouton precedent du navigateur (cache avant/arriere) : le menu ne doit pas rester ouvert */
    onPageShow(event) {
      if (!event || !event.persisted || !this.ready) return;
      this.clearBusy();
      if (this.isOpen()) this.close(true);
    }

    /* Le bouton du menu a disparu (passage a la navigation de bureau) : on referme */
    onResize() {
      if (this.ready && this.isOpen() && !this.trigger.getClientRects().length) this.close(true);
    }
  }

  customElements.define('vt-menu', VtMenu);
})();